import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";
import { fetchSports } from "@/lib/sportsCatalog";

const TOP_LEVEL_KEYS = ["position", "foot", "nationality", "height_cm"];
const IDENTITY_SPORT_KEYS = ["nationality"]; // shown in identity section, hidden from sport profile

function fieldValue(player, key) {
  if (TOP_LEVEL_KEYS.includes(key)) {
    const v = player[key];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return (player.sport_data || {})[key];
}

function Section({ title, action, children, testid }) {
  return (
    <section className="rounded-3xl border border-border dark:border-white/10 bg-card dark:bg-surface p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-foreground/85">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function RField({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{label}</div>
      <div className={cn("text-sm font-medium mt-1", (value === null || value === undefined || value === "") && "text-muted-foreground/50")}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </div>
    </div>
  );
}

function FField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

const inputCls = "bg-background/60 border-border dark:border-white/10";

export default function AthleteInfoTab({ player, editMode, setEditMode, onUpdated }) {
  const { tf, locale } = useLang();
  const [sports, setSports] = useState(null);
  const [saving, setSaving] = useState(false);

  const initIdentity = () => ({
    name: player.name || "",
    birth_date: player.birth_date || "",
    age: player.age ?? "",
    nationality: player.nationality || "",
    phone: player.phone || "",
    email: player.email || "",
  });
  const initPro = () => ({
    sport: player.sport || "football",
    club: player.club || "",
    competition: player.competition || "",
    level: player.level || "",
    secondary_positions: player.secondary_positions || "",
    about: player.about || "",
  });
  const [identity, setIdentity] = useState(initIdentity);
  const [pro, setPro] = useState(initPro);
  const [sportValues, setSportValues] = useState({});

  useEffect(() => {
    fetchSports().then(setSports).catch(() => toast.error(tf("Impossible de charger le catalogue des sports")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sport = useMemo(
    () => (sports || []).find((s) => s.id === (editMode ? pro.sport : player.sport || "football")),
    [sports, pro.sport, player.sport, editMode]
  );

  const sportFields = useMemo(
    () => (sport?.fields || []).filter((f) => !IDENTITY_SPORT_KEYS.includes(f.key)),
    [sport]
  );

  // (Re)hydrate the form whenever edit mode opens or the player changes
  useEffect(() => {
    if (!editMode) return;
    setIdentity(initIdentity());
    setPro(initPro());
    const sv = {};
    const cfg = (sports || []).find((s) => s.id === (player.sport || "football"));
    (cfg?.fields || []).forEach((f) => {
      if (IDENTITY_SPORT_KEYS.includes(f.key)) return;
      const v = fieldValue(player, f.key);
      sv[f.key] = v === undefined || v === null ? "" : String(v);
    });
    setSportValues(sv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, player, sports]);

  const setId = (k, v) => setIdentity((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setPro((f) => ({ ...f, [k]: v }));
  const setSV = (k, v) => setSportValues((f) => ({ ...f, [k]: v }));

  const changeSport = (id) => {
    if (id === pro.sport) return;
    const hasValues = Object.values(sportValues).some((v) => v !== "" && v != null) || !!pro.competition;
    if (hasValues && !window.confirm(tf("Changer de sport effacera les champs spécifiques déjà saisis. Continuer ?"))) return;
    setPro((f) => ({ ...f, sport: id, competition: "" }));
    setSportValues({});
  };

  const save = async () => {
    if (!identity.name.trim()) { toast.error(tf("Nom requis")); return; }
    if (identity.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(identity.email.trim())) {
      toast.error(tf("Email invalide")); return;
    }
    setSaving(true);
    try {
      const sd = {};
      (sport?.fields || []).forEach((f) => {
        if (IDENTITY_SPORT_KEYS.includes(f.key)) return;
        const raw = sportValues[f.key];
        if (raw === "" || raw == null) return;
        sd[f.key] = f.type === "number" ? Number(raw) : raw;
      });
      const position = sd.position ?? ""; delete sd.position;
      const foot = sd.foot ?? ""; delete sd.foot;
      const height = sd.height_cm; delete sd.height_cm;
      const payload = {
        name: identity.name.trim(),
        birth_date: identity.birth_date,
        age: identity.age === "" ? null : Number(identity.age),
        nationality: identity.nationality.trim(),
        phone: identity.phone.trim(),
        email: identity.email.trim(),
        club: pro.club.trim(),
        level: pro.level.trim(),
        secondary_positions: pro.secondary_positions,
        about: pro.about,
        sport: pro.sport,
        competition: pro.competition,
        position,
        foot,
        height_cm: Number.isFinite(height) ? height : null,
        sport_data: sd,
      };
      await api.patch(`/players/${player.id}`, payload);
      toast.success(tf("Modifications enregistrées"));
      setEditMode(false);
      onUpdated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tf("Erreur"));
    } finally { setSaving(false); }
  };

  const competitionLabel = (id) => {
    const c = (sport?.competitions || []).find((x) => x.id === id);
    return c ? c.label : id || "";
  };

  const fmtDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString(locale); } catch { return d; }
  };

  /* ============ READ-ONLY ============ */
  if (!editMode) {
    return (
      <div className="space-y-4">
        <Section title={tf("Informations personnelles")} testid="card-info-readonly" action={
          <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="h-8 gap-1.5 border-border dark:border-white/10" data-testid="info-edit-btn">
            <Pencil className="h-3.5 w-3.5" /> {tf("Modifier")}
          </Button>
        }>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            <RField label={tf("Nom complet")} value={player.name} testid="ro-name" />
            <RField label={tf("Date de naissance")} value={fmtDate(player.birth_date)} testid="ro-birthdate" />
            <RField label={tf("Âge")} value={player.age} testid="ro-age" />
            <RField label={tf("Nationalité")} value={player.nationality} testid="ro-nationality" />
            <RField label={tf("Téléphone")} value={player.phone} testid="ro-phone" />
            <RField label={tf("Email")} value={player.email} testid="ro-email" />
          </div>
        </Section>

        <Section title={tf("Informations professionnelles")} testid="card-info-pro">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            <RField label={tf("Sport")} value={sport ? tf(sport.label) : tf(player.sport || "Football")} testid="ro-sport" />
            <RField label={tf(sport?.club_label || "Club actuel")} value={player.club} testid="ro-club" />
            <RField label={tf(sport?.competition_label || "Ligue")} value={tf(competitionLabel(player.competition))} testid="ro-competition" />
            <RField label={tf("Niveau actuel")} value={player.level} testid="ro-level" />
            <RField label={tf("Postes secondaires")} value={player.secondary_positions} testid="ro-secondary" />
          </div>
        </Section>

        {sportFields.length > 0 && (
          <Section title={tf("Profil sportif")} testid="card-info-sport">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
              {sportFields.map((f) => {
                const v = fieldValue(player, f.key);
                return <RField key={f.key} label={tf(f.label)} value={v === undefined || v === null || v === "" ? "" : (f.type === "select" ? tf(String(v)) : String(v))} testid={`ro-${f.key}`} />;
              })}
            </div>
          </Section>
        )}

        {player.about && (
          <Section title={tf("À propos")} testid="card-info-about">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{player.about}</p>
          </Section>
        )}
      </div>
    );
  }

  /* ============ EDIT MODE ============ */
  return (
    <div className="space-y-4" data-testid="card-info-edit">
      <Section title={tf("Informations personnelles")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FField label={tf("Nom complet")}><Input value={identity.name} onChange={(e) => setId("name", e.target.value)} className={inputCls} data-testid="edit-name" /></FField>
          <FField label={tf("Date de naissance")}><Input type="date" value={identity.birth_date} onChange={(e) => setId("birth_date", e.target.value)} className={cn(inputCls, "min-w-0 w-full")} data-testid="edit-birthdate" /></FField>
          <FField label={tf("Âge")}><Input type="number" min="10" max="60" value={identity.age} onChange={(e) => setId("age", e.target.value)} className={inputCls} data-testid="edit-age" /></FField>
          <FField label={tf("Nationalité")}><Input value={identity.nationality} onChange={(e) => setId("nationality", e.target.value)} className={inputCls} data-testid="edit-nationality" /></FField>
          <FField label={tf("Téléphone")}><Input value={identity.phone} onChange={(e) => setId("phone", e.target.value)} className={inputCls} data-testid="edit-phone" /></FField>
          <FField label={tf("Email")}><Input type="email" value={identity.email} onChange={(e) => setId("email", e.target.value)} className={inputCls} data-testid="edit-email" /></FField>
        </div>
      </Section>

      <Section title={tf("Informations professionnelles")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FField label={tf("Sport")}>
            <Select value={pro.sport} onValueChange={changeSport}>
              <SelectTrigger className={inputCls} data-testid="info-sport-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {(sports || []).map((s) => <SelectItem key={s.id} value={s.id} data-testid={`info-sport-${s.id}`}>{tf(s.label)}</SelectItem>)}
              </SelectContent>
            </Select>
          </FField>
          <FField label={tf(sport?.competition_label || "Ligue")}>
            <Select value={pro.competition || "__none__"} onValueChange={(v) => setP("competition", v === "__none__" ? "" : v)}>
              <SelectTrigger className={inputCls} data-testid="info-competition-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">—</SelectItem>
                {(sport?.competitions || []).map((c) => <SelectItem key={c.id} value={c.id}>{tf(c.label)}</SelectItem>)}
              </SelectContent>
            </Select>
          </FField>
          <FField label={tf(sport?.club_label || "Club actuel")}><Input value={pro.club} onChange={(e) => setP("club", e.target.value)} className={inputCls} data-testid="edit-club" /></FField>
          <FField label={tf("Niveau actuel")}><Input value={pro.level} onChange={(e) => setP("level", e.target.value)} className={inputCls} placeholder="N3, N2, L2…" data-testid="edit-level" /></FField>
          <FField label={tf("Postes secondaires")}><Input value={pro.secondary_positions} onChange={(e) => setP("secondary_positions", e.target.value)} className={inputCls} data-testid="edit-secondary" /></FField>
        </div>
      </Section>

      {sportFields.length > 0 && (
        <Section title={tf("Profil sportif")}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sportFields.map((f) => (
              <FField key={f.key} label={<>{tf(f.label)}{f.required ? <span className="text-athlynx ml-0.5">*</span> : null}</>}>
                {f.type === "select" ? (
                  <Select value={sportValues[f.key] || "__none__"} onValueChange={(v) => setSV(f.key, v === "__none__" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid={`info-field-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__none__">—</SelectItem>
                      {(f.options || []).map((o) => <SelectItem key={o} value={o}>{tf(o)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    value={sportValues[f.key] ?? ""}
                    onChange={(e) => setSV(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    className={inputCls}
                    data-testid={`info-field-${f.key}`}
                  />
                )}
              </FField>
            ))}
          </div>
        </Section>
      )}

      <Section title={tf("À propos")}>
        <Textarea value={pro.about} onChange={(e) => setP("about", e.target.value)} className={cn(inputCls, "min-h-[100px]")} placeholder={tf("Description, points forts, contexte…")} data-testid="edit-about" />
        <div className="flex items-center gap-2 mt-5">
          <Button onClick={save} disabled={saving} className="h-10 px-5 bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid="info-save-btn">
            <Save className="h-4 w-4" /> {saving ? tf("Enregistrement…") : tf("Enregistrer")}
          </Button>
          <Button variant="ghost" onClick={() => setEditMode(false)} className="h-10" data-testid="info-cancel-btn">{tf("Annuler")}</Button>
        </div>
      </Section>
    </div>
  );
}
